import Utils from "@/core/utils/Utils";

/**
 * Базовый класс для сериализуемых объектов
 *
 * Наследование:
 * - Используйте extends если возможно
 * - Если наследование недоступно, скопируйте методы:
 *   static fromServer = Serializable.fromServer;
 *   toJSON = Serializable.prototype.toJSON;
 *
 * Для использования fromServer как колбэка (например в Utils.hydrateArray),
 * наследник должен переопределить метод с явным созданием инстанса:
 *   static fromServer(data: any, result = new Person()): Person {
 *       super.fromServer(data, result);
 *       return result;
 *   }
 */
class Serializable {

    /** Поля помеченные декоратором @serializeSimplified */
    static __simplifyFields?: string[];

    /** Поля помеченные декоратором @noSerialize */
    static __noSerializeFields?: string[];

    /**
     * Статический метод создания объекта из данных сервера
     * @param data - данные с сервера
     * @param result - экземпляр класса
     * @returns экземпляр класса
     */
    static fromServer(data: any, result: Serializable = new this()): Serializable {
        // проходим результирующий объект по иерархии потому, что если идти по исходному, то получить дескриптор не получится
        // потому что дескриптор надо получать по всей иерархии
        let obj: any = result;

        while (Object.getPrototypeOf(obj) !== null) {
            for (const fieldName of Object.getOwnPropertyNames(obj)) {
                const fieldDescriptor = Object.getOwnPropertyDescriptor(obj, fieldName);

                // проверяем что это не функция и поле присутствует в исходном объекте
                if (
                    fieldDescriptor
                    && typeof fieldDescriptor.value !== 'function'
                    && typeof data[fieldName] !== 'undefined'
                ) {
                    let targetFieldName = fieldName;
                    // если есть геттер или сеттер то к имени поля добавляем _ чтобы обойти сеттер
                    if (
                        fieldDescriptor
                        && (typeof fieldDescriptor.get === 'function' || typeof fieldDescriptor.set === 'function')
                    ) {
                        targetFieldName = `_${fieldName}`;
                    }

                    (result as any)[targetFieldName] = data[fieldName];
                }
            }
            obj = Object.getPrototypeOf(obj);
        }

        return result;
    }

    toJSON(fieldsToSimplify: string[] = []): Record<string, any> {
        const result: Record<string, any> = {};

        // Собираем поля из декораторов @serializeSimplified и @noSerialize по всей цепочке наследования
        const decoratedFields: string[] = [];
        const noSerializeFields: string[] = [];
        let proto: any = this.constructor;

        while (proto) {
            if (proto.__simplifyFields) {
                decoratedFields.push(...proto.__simplifyFields);
            }
            if (proto.__noSerializeFields) {
                noSerializeFields.push(...proto.__noSerializeFields);
            }
            proto = Object.getPrototypeOf(proto);
        }

        // Объединяем поля из параметра и из декораторов
        const simplifySet = new Set([
            ...(Array.isArray(fieldsToSimplify) ? fieldsToSimplify : [fieldsToSimplify]),
            ...decoratedFields,
        ]);

        // Поля исключаемые из сериализации
        const noSerializeSet = new Set(noSerializeFields);
        // проходим по всей иерархии, потому что вариант с OBJECT.entries сработает
        // только если всем protected полям соответствуют поля с геттерами
        let obj: any = this;

        while (Object.getPrototypeOf(obj) !== null) {
            for (const fieldName of Object.getOwnPropertyNames(obj)) {
                const fieldDescriptor = Object.getOwnPropertyDescriptor(obj, fieldName);
                // если имя поля содержит подчерк то оно приватное, пропускаем
                // ну или если это функция тоже пропускаем
                // ну или если поле помечено декоратором @noSerialize, тоже пропускаем
                // ВАЖНО: геттеры прикидываются свойствами объектов
                // например: Object._testField у которого есть геттер таким образом в списке свойст мы получим
                // 2 записи: testField и _testField, второй мы пропустим по условию ниже
                if (
                    fieldName.substring(0, 1) === '_'
                    || (fieldDescriptor && typeof fieldDescriptor.value === 'function')
                    || noSerializeSet.has(fieldName)
                ) {
                    continue;
                }


                // Конвертируем undefined в null
                const value = (this as any)[fieldName] ?? null;


                // Упрощаем сразу при присваивании, если поле помечено декоратором или в списке
                result[fieldName] = simplifySet.has(fieldName) && value
                    ? Utils.simplifyObjectForServer(Utils.removeCircular(value, 3))
                    : value;

            }
            obj = Object.getPrototypeOf(obj);
        }

        return result;
    }
}

export default Serializable;